import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { forgotPassword, clearErrors, clearMessage } from "../slices/forgotPasswordSlice";

const ForgotPassword = () => {
  const dispatch = useDispatch();
  useNavigate();
  const { loading, message } = useSelector((state) => state.forgotPassword);

  const [formData, setFormData] = useState({
    email: "",
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    dispatch(clearErrors());
    dispatch(clearMessage());
  }, [dispatch]);

  const validateForm = () => {
    const errors = {};

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await dispatch(forgotPassword({ email: formData.email })).unwrap();
    } catch (err) {
      // Error is handled by the slice with toast
      console.error("Forgot password error:", err);
    }
  };

  return (
    <div style={{
      ...styles.container,
      padding: isMobile ? "16px" : "20px",
    }}>
      <div style={{
        ...styles.card,
        padding: isMobile ? "24px 16px" : "40px",
        maxWidth: isMobile ? "100%" : "400px",
      }}>
        <h1 style={{
          ...styles.title,
          fontSize: isMobile ? "24px" : "28px",
        }}>Forgot Password</h1>
        <p style={styles.subtitle}>
          Enter your email address and we'll send you a link to reset your password.
        </p>

        {message ? (
          <div style={styles.successMessage}>
            <p style={styles.successText}>{message}</p>
            <p style={styles.successSubtext}>
              Please check your email for the password reset link.
            </p>
            <Link to="/login" style={styles.backLink}>
              Back to Login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{
            ...styles.form,
            gap: isMobile ? "16px" : "20px",
          }}>
            <div style={styles.formGroup}>
              <label htmlFor="email" style={styles.label}>
                Email Address <span style={styles.required}>*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  borderColor: validationErrors.email ? "#dc3545" : "#e9edef",
                  width: "100%",
                }}
                placeholder="Enter your email"
                disabled={loading}
              />
              {validationErrors.email && (
                <span style={styles.errorText}>{validationErrors.email}</span>
              )}
            </div>

            <button
              type="submit"
              style={{
                ...styles.button,
                padding: isMobile ? "12px 20px" : "12px 24px",
              }}
              disabled={loading}
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </button>
          </form>
        )}

        {!message && (
          <div style={{
            ...styles.footer,
            marginTop: isMobile ? "16px" : "20px",
          }}>
            <Link to="/login" style={styles.link}>
              Back to Login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f0f2f5",
    padding: "20px",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, 'Lucida Grande', Arial, Ubuntu, Cantarell, 'Fira Sans', sans-serif",
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
    padding: "40px",
    width: "100%",
    maxWidth: "400px",
    border: "1px solid #e9edef",
  },
  title: {
    fontSize: "28px",
    fontWeight: "500",
    marginBottom: "8px",
    color: "#111b21",
    textAlign: "center",
    letterSpacing: "-0.02em",
  },
  subtitle: {
    fontSize: "14px",
    color: "#667781",
    marginBottom: "30px",
    textAlign: "center",
    fontWeight: "400",
    letterSpacing: "-0.01em",
    lineHeight: "20px",
  },
  form: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
  },
  label: {
    display: "block",
    fontSize: "14px",
    fontWeight: "500",
    color: "#111b21",
    letterSpacing: "-0.01em",
  },
  required: {
    color: "#dc3545",
  },
  input: {
    width: "100%",
    padding: "12px 16px",
    fontSize: "15px",
    fontWeight: "400",
    border: "1px solid #e9edef",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    outline: "none",
    transition: "border-color 0.2s",
    color: "#111b21",
    letterSpacing: "-0.01em",
    boxSizing: "border-box",
  },
  inputError: {
    borderColor: "#dc3545",
  },
  errorText: {
    color: "#dc3545",
    fontSize: "12px",
    marginTop: "4px",
    display: "block",
    fontWeight: "400",
  },
  button: {
    width: "100%",
    padding: "12px 24px",
    fontSize: "15px",
    fontWeight: "400",
    color: "#ffffff",
    backgroundColor: "#dc3545",
    border: "none",
    borderRadius: "24px",
    cursor: "pointer",
    transition: "background-color 0.2s",
    marginTop: "10px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    letterSpacing: "-0.01em",
  },
  successMessage: {
    textAlign: "center",
    padding: "20px 0",
  },
  successText: {
    fontSize: "16px",
    color: "#25d366",
    fontWeight: "400",
    marginBottom: "12px",
    letterSpacing: "-0.01em",
  },
  successSubtext: {
    fontSize: "14px",
    color: "#667781",
    marginBottom: "24px",
    fontWeight: "400",
    letterSpacing: "-0.01em",
    lineHeight: "20px",
  },
  backLink: {
    display: "inline-block",
    padding: "12px 24px",
    backgroundColor: "#dc3545",
    color: "#ffffff",
    textDecoration: "none",
    borderRadius: "24px",
    fontWeight: "400",
    fontSize: "15px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    letterSpacing: "-0.01em",
  },
  footer: {
    marginTop: "20px",
    textAlign: "center",
    fontSize: "14px",
    paddingTop: "20px",
    borderTop: "1px solid #e9edef",
  },
  link: {
    color: "#00a884",
    textDecoration: "none",
    fontWeight: "400",
    letterSpacing: "-0.01em",
  },
};

export default ForgotPassword;

